import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { Observable, switchMap, tap } from 'rxjs';
import { FileSharingService } from 'src/app/services/file-sharing.service';
import { FileSharing } from 'src/app/types/file-sharing.type';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { formHasAllEmptyValues } from 'src/app/helpers/form-has-all-empty-values.helper';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { trimValues } from 'src/app/helpers/trim-form-values.helper';
import { datePickerTimeControl } from 'src/app/helpers/date-picker-time-control.helper';

@Component({
  selector: 'app-file-sharing',
  templateUrl: './file-sharing.component.html',
  styleUrls: ['./file-sharing.component.scss'],
  standalone: true,
  imports: [
    NzPageHeaderModule,
    NzButtonModule,
    NzTableModule,
    DatePipe,
    NzBadgeModule,
    NzIconModule,
    NzTooltipModule,
    NzDividerModule,
    AsyncPipe,
    NzModalModule,
    NzFormModule,
    NzDatePickerModule,
    ReactiveFormsModule,
    NzInputModule,
    RouterLink,
  ],
})
export class FileSharingComponent {
  private fileSharingService = inject(FileSharingService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(FormBuilder);

  pageNumber = 1;
  pageSize = 20;
  id = null;
  startAt = null;
  endAt = null;
  isShowBadge = false;
  isFilterModalVisible = false;
  withTotal = true;

  files: FileSharing[] = [];
  filterForm = this.fb.group({
    id: this.fb.control<string | null>(null),
    startAt: this.fb.control<Date | null>(null),
    endAt: this.fb.control<Date | null>(null),
  });

  fileSharingTotal = this.fileSharingService.fileSharingTotal;

  queryParams$ = this.getQueryParams();

  startAtDateChanged$ = datePickerTimeControl(
    this.filterForm.get('startAt')!,
    false,
  ).pipe(tap((date) => this.filterForm.patchValue({ startAt: date })));

  endAtDateChanged$ = datePickerTimeControl(
    this.filterForm.get('endAt')!,
    true,
  ).pipe(tap((date) => this.filterForm.patchValue({ endAt: date })));

  getFileSharing(pageNumber: number): Observable<FileSharing[]> {
    const params = {
      pageNumber,
      pageSize: this.pageSize,
      ...(this.filterForm.value as any),
    };

    return this.fileSharingService.getSharedFiles(params).pipe(
      tap((files) => {
        this.files = files;
      }),
    );
  }

  getQueryParams(): Observable<FileSharing[]> {
    return this.activatedRoute.queryParamMap.pipe(
      switchMap((params) => {
        const pageNumber = Number(params.get('pageNumber'));
        const pageSize = Number(params.get('pageSize'));
        const endAt = params.get('endAt');
        const id = params.get('id');
        const startAt = params.get('startAt');

        if (!!pageSize) this.pageSize = Number(params.get('pageSize'));

        this.pageNumber = isNaN(pageNumber) || pageNumber < 1 ? 1 : pageNumber;

        this.filterForm.patchValue({
          id: id,
          endAt: endAt ? new Date(endAt) : null,
          startAt: startAt ? new Date(startAt) : null,
        });

        formHasAllEmptyValues(this.filterForm)
          ? (this.isShowBadge = false)
          : (this.isShowBadge = true);

        return this.getFileSharing(this.pageNumber);
      }),
    );
  }

  goToCreateFileSharing(): void {
    this.router.navigate(['/file-sharing/create'], {
      queryParamsHandling: 'preserve',
    });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex } = params;

    const { id, startAt, endAt } = this.filterForm.value;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        pageNumber: params.pageSize !== this.pageSize ? 1 : pageIndex,
        pageSize: params.pageSize,
        id,
        startAt,
        endAt,
      },
      queryParamsHandling: 'merge',
    });
  }

  filterModalCancel(): void {
    this.isShowBadge = false;
    this.filterForm.reset();
    this.onQueryParamsChange({
      pageIndex: 1,
      pageSize: this.pageSize,
    } as NzTableQueryParams);
    this.withTotal = true;
    this.queryParams$ = this.getQueryParams();
    this.closeFilterModal();
  }

  filterModalOk(): void {
    formHasAllEmptyValues(this.filterForm)
      ? (this.isShowBadge = false)
      : (this.isShowBadge = true);

    if (this.filterForm.value.id?.length === 0) {
      this.filterForm.patchValue({ id: null });
    }

    trimValues(this.filterForm);

    this.onQueryParamsChange({
      pageIndex: 1,
      pageSize: this.pageSize,
    } as NzTableQueryParams);
    this.withTotal = true;
    this.queryParams$ = this.getQueryParams();
    this.closeFilterModal();
  }

  showFilterModal(): void {
    this.isFilterModalVisible = true;
  }

  closeFilterModal(): void {
    this.isFilterModalVisible = false;
  }

  reloadPage(): void {
    this.withTotal = true;
    this.queryParams$ = this.getQueryParams();
  }

  setEndDate(date: Date): Date {
    const currentDay = date.getDate();

    date.setDate(currentDay + 1);

    return date;
  }
}
